import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './BankConnected.css';
import PageLayout from './common/PageLayout';
import { useTranslation } from 'react-i18next';
import { getEnvironmentParams } from '../utils/auth';
import { apiUrl, envConfig, getCurrentEnv } from '../config/envConfig';
import { setReturnUrl, returnWithBcStart } from '../utils/returnToHost';

const FAILURE_REDIRECT_DELAY_MS = 2000;

// Capture pre-bank handoff parameters at module load — before any React mount
// or useTokenHandler URL clean. The WC redirects users here with the consent
// + auth context as query params and `?bankUrl=<scaOAuthLink>` indicating
// "stash this and forward to the bank". The same /bank-connected route then
// handles the post-bank callback (with `?code=…`) using the stashed data.
//
// Reliability trick: `bankUrl` is also persisted to sessionStorage as
// `pendingBankUrl`. That way, even if React's effect cycle is interrupted
// (StrictMode double-mount in dev, hot reload, slow render, …) any later
// mount can recover the redirect target and finish the handoff.
let pendingHandoff = null;
try {
    if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const bankUrl = params.get('bankUrl');

        // Reaching /bank-connected with `code` or `error` means we're on the
        // post-bank callback. The handoff is over; wipe any stale pending
        // redirect so we don't accidentally bounce back to the bank.
        if (params.get('code') || params.get('error')) {
            try { sessionStorage.removeItem('pendingBankUrl'); } catch {}
        }

        if (bankUrl) {
            pendingHandoff = {
                bankUrl,
                token: params.get('token'),
                consentId: params.get('consentId'),
                authorizationId: params.get('authorizationId'),
                bicFi: params.get('bicFi'),
                psuId: params.get('psuId'),
                bankName: params.get('bankName'),
                countryName: params.get('countryName'),
                countryFlag: params.get('countryFlag'),
                countryCode: params.get('countryCode'),
                redirectUrl: params.get('redirectUrl'),
            };
            // Apply the handoff stash immediately so even if React renders late
            // or a sibling effect cleans the URL, session state is already set.
            try {
                sessionStorage.setItem('pendingBankUrl', bankUrl);
                if (pendingHandoff.token) {
                    let existing = {};
                    try { existing = JSON.parse(localStorage.getItem('appParams') || '{}'); } catch {}
                    localStorage.setItem('appParams', JSON.stringify({ ...existing, token: pendingHandoff.token }));
                }
                if (pendingHandoff.consentId && pendingHandoff.authorizationId) {
                    sessionStorage.setItem('consentData', JSON.stringify({
                        consentId: pendingHandoff.consentId,
                        authorizationId: pendingHandoff.authorizationId,
                    }));
                }
                if (pendingHandoff.bicFi || pendingHandoff.psuId || pendingHandoff.bankName) {
                    sessionStorage.setItem('selectedBank', JSON.stringify({
                        bicFi: pendingHandoff.bicFi || undefined,
                        socialSecurityNumber: pendingHandoff.psuId || undefined,
                        name: pendingHandoff.bankName || undefined,
                    }));
                }
                if (pendingHandoff.countryName || pendingHandoff.countryFlag || pendingHandoff.countryCode) {
                    sessionStorage.setItem('selectedCountry', JSON.stringify({
                        name: pendingHandoff.countryName || undefined,
                        flagCode: pendingHandoff.countryFlag || undefined,
                        code: pendingHandoff.countryCode || undefined,
                    }));
                }
                if (pendingHandoff.redirectUrl) {
                    try { sessionStorage.setItem('obfeRedirectUrl', pendingHandoff.redirectUrl); } catch {}
                }
            } catch {}
        } else if (!params.get('code') && !params.get('error')) {
            // No bankUrl in the URL and not a post-bank callback either.
            // Recover any pending redirect target that an earlier mount may
            // have stashed but failed to fire (hot reload / dev quirks).
            let recovered = null;
            try { recovered = sessionStorage.getItem('pendingBankUrl'); } catch {}
            if (recovered) {
                pendingHandoff = { bankUrl: recovered };
            }
        }
    }
} catch {}

// Visual screen for the brief moment we're forwarding the user to the bank.
// Owns its own watchdog so even if the parent's effect-scheduled redirect
// somehow drops, the user has both an auto-retry and a manual escape hatch.
function HandoffSpinner({ bankUrl, t }) {
    const [showManual, setShowManual] = useState(false);

    useEffect(() => {
        if (!bankUrl) return;
        // 1.5s watchdog: if we're still on this screen, retry the redirect
        // and surface the manual link to the user.
        const watchdog = setTimeout(() => {
            try { sessionStorage.removeItem('pendingBankUrl'); } catch {}
            try { window.location.assign(bankUrl); } catch {}
            setShowManual(true);
        }, 1500);
        return () => clearTimeout(watchdog);
    }, [bankUrl]);

    const continueManually = () => {
        if (!bankUrl) return;
        try { sessionStorage.removeItem('pendingBankUrl'); } catch {}
        window.location.assign(bankUrl);
    };

    return (
        <PageLayout currentStep={2} navigationProps={{ showPrevious: false, showNext: false }}>
            <style>{`@keyframes obfe-bc-handoff-spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ maxWidth: 480, margin: '0 auto', padding: '64px 24px', textAlign: 'center' }}>
                <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    border: '4px solid #dcfce7', borderTopColor: '#166534',
                    animation: 'obfe-bc-handoff-spin 0.9s linear infinite',
                    margin: '0 auto 24px',
                }} />
                <h1 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: '#111' }}>
                    {t('bankConnectedHandoffTitle', 'Redirecting to your bank…')}
                </h1>
                <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.5, margin: '0 0 24px' }}>
                    {t('bankConnectedHandoffDesc', "Hold on — we're sending you to your bank to authorise the connection. Please don't close this tab.")}
                </p>
                {bankUrl && (
                    <button
                        type="button"
                        onClick={continueManually}
                        style={{
                            padding: '10px 18px', borderRadius: 6, border: '1px solid #166534',
                            background: showManual ? '#166534' : '#fff',
                            color: showManual ? '#fff' : '#166534',
                            fontSize: 14, fontWeight: 600, cursor: 'pointer',
                        }}
                    >
                        {showManual
                            ? t('bankConnectedHandoffOpenNow', 'Open your bank now')
                            : t('bankConnectedHandoffOpenManually', 'Not redirecting? Open manually')}
                    </button>
                )}
            </div>
        </PageLayout>
    );
}

function BankConnected() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [bankingInfo, setBankingInfo] = useState({ country: null, bank: null });
    const [isActivating, setIsActivating] = useState(false);
    const [activationError, setActivationError] = useState(null);
    const [oauthError, setOauthError] = useState(null);
    const [isHandingOff] = useState(!!pendingHandoff);
    const activationAttemptedRef = useRef(false);
    const codeProcessedRef = useRef(false);
    const failureRedirectTimerRef = useRef(null);
    const handoffFiredRef = useRef(false);

    useEffect(() => {
        const storedCountry = sessionStorage.getItem('selectedCountry');
        const storedBank = sessionStorage.getItem('selectedBank');

        if (storedCountry && storedBank) {
            try {
                const countryData = JSON.parse(storedCountry);
                const bankData = JSON.parse(storedBank);
                setBankingInfo({ country: countryData, bank: bankData });
            } catch (error) {
                console.error('Error parsing stored banking info:', error);
            }
        }
    }, []);

    const scheduleFailureRedirect = () => {
        if (failureRedirectTimerRef.current) return;
        failureRedirectTimerRef.current = setTimeout(() => {
            returnWithBcStart('/aspspsList');
        }, FAILURE_REDIRECT_DELAY_MS);
    };

    const tryAgainNow = () => {
        if (failureRedirectTimerRef.current) {
            clearTimeout(failureRedirectTimerRef.current);
            failureRedirectTimerRef.current = null;
        }
        returnWithBcStart('/aspspsList');
    };

    useEffect(() => {
        return () => {
            if (failureRedirectTimerRef.current) {
                clearTimeout(failureRedirectTimerRef.current);
                failureRedirectTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        // Pre-bank handoff phase: WC sent us here with the bank's OAuth URL
        // in `?bankUrl=` (or we recovered it from sessionStorage). Session
        // data was already stashed at module load. Fire the redirect via
        // double-rAF so the spinner paints first, then navigate.
        if (pendingHandoff && !handoffFiredRef.current) {
            handoffFiredRef.current = true;
            const bankUrl = pendingHandoff.bankUrl;
            // Drop the handoff params so a refresh / back-button doesn't replay
            // the redirect to the bank.
            try {
                const cleanUrl = window.location.pathname;
                window.history.replaceState({}, document.title, cleanUrl);
            } catch {}
            // NOTE: do NOT null `pendingHandoff` here — if this effect's
            // navigation gets dropped (StrictMode remount, hot reload, throttled
            // background tab, …) the spinner-screen's own fallback (below) can
            // still recover from sessionStorage.pendingBankUrl.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    try { sessionStorage.removeItem('pendingBankUrl'); } catch {}
                    window.location.assign(bankUrl);
                });
            });
            return;
        }

        const searchParams = new URLSearchParams(window.location.search);
        const code = searchParams.get('code');
        const scope = searchParams.get('scope');
        const state = searchParams.get('state');
        const oauthErrorParam = searchParams.get('error');
        const oauthErrorDescription = searchParams.get('error_description');

        // Persist the return URL (or refresh-recover it) before we clean the URL.
        const incomingRedirectUrl = searchParams.get('redirectUrl');
        if (incomingRedirectUrl) setReturnUrl(incomingRedirectUrl);

        if (window.location.search) {
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
        }

        // OAuth-level failure (user cancelled, bank denied, …) — never call activation.
        if (oauthErrorParam) {
            const message = oauthErrorDescription || oauthErrorParam;
            setOauthError(message);
            scheduleFailureRedirect();
            return;
        }

        if (code && !codeProcessedRef.current && !activationAttemptedRef.current) {
            codeProcessedRef.current = true;
            activationAttemptedRef.current = true;
            activateAuthorization(code, scope, state, incomingRedirectUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const activateAuthorization = async (code, scope, state, redirectUrlOverride) => {
        setIsActivating(true);
        setActivationError(null);

        try {
            const consentDataStr = sessionStorage.getItem('consentData');
            if (!consentDataStr) {
                throw new Error('No consent data found in session storage');
            }

            const consentData = JSON.parse(consentDataStr);
            const consentId = consentData.consentId;
            const authorizationId = consentData.authorizationId;

            if (!consentId || !authorizationId) {
                throw new Error('Missing consentId or authorizationId in consent data');
            }

            const params = getEnvironmentParams();
            if (!params.token) {
                throw new Error('No authentication token found');
            }

            const bankData = JSON.parse(sessionStorage.getItem('selectedBank') || '{}');

            const userAgent = navigator.userAgent;
            let userIp = '127.0.0.1';

            try {
                const ipResponse = await fetch('https://api.ipify.org?format=json');
                const ipData = await ipResponse.json();
                userIp = ipData.ip;
            } catch (err) {
                console.error('Error fetching IP address:', err);
            }

            const activationUrl = `${apiUrl}/consents/${consentId}/activateauthorization/${authorizationId}/${code}`;

            const baseRedirectUri = envConfig[getCurrentEnv()].redirectUri;
            const redirectUriHeader = redirectUrlOverride
                ? `${baseRedirectUri}?redirectUrl=${redirectUrlOverride}`
                : baseRedirectUri;

            const headers = {
                'Content-Type': 'application/json',
                'BicFi': bankData?.bicFi || 'ESSESESS',
                'Psu-Id': bankData?.socialSecurityNumber,
                'Psu-Ip-Address': userIp,
                'Psu-User-Agent': userAgent,
                'Authorization': `Bearer ${params.token}`,
                'Tpp-Redirect-Preferred': false,
                'Redirect-Uri': redirectUriHeader,
            };

            const response = await fetch(activationUrl, {
                method: 'POST',
                headers: headers,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = errorData.message ? ` ${errorData.message}` : '';
                throw new Error(`HTTP error! status: ${response.status}${errorMessage}`);
            }

            const responseData = await response.json().catch(() => ({}));
            if (responseData && Object.keys(responseData).length > 0) {
                sessionStorage.setItem('consentData', JSON.stringify({ ...consentData, ...responseData }));
            }

            // Success — openbankingfe owns the next step (pick account → summary).
            navigate('/pick-account');

        } catch (err) {
            console.error('Error activating authorization:', err);
            setActivationError(err.message);
            scheduleFailureRedirect();
        } finally {
            setIsActivating(false);
        }
    };

    const isFailure = !!oauthError || !!activationError;
    const failureTitle = oauthError
        ? t('bankConnectedOauthCancelledTitle', 'Authorization cancelled')
        : t('bankConnectedActivationFailedTitle', 'Could not activate consent');
    const failureBody = oauthError || activationError;

    const navigationProps = isFailure
        ? {
            previousPath: '/bank-connection',
            showNext: true,
            nextLabel: t('bankConnectedTryAgain', 'Try again'),
            onNext: tryAgainNow,
        }
        : {
            previousPath: '/bank-connection',
            showNext: false,
        };

    const displayBankName = bankingInfo.bank?.name || 'bank';

    if (isHandingOff) {
        return <HandoffSpinner bankUrl={pendingHandoff?.bankUrl} t={t} />;
    }

    return (
        <PageLayout currentStep={2} navigationProps={navigationProps}>
            <div className="bank-connected-content">
                <div className="bank-connected-icon">
                    <span role="img" aria-label="bank" style={{ fontSize: '36px' }}>
                        {isFailure ? '⚠️' : '🏛️'}
                    </span>
                </div>

                <h1 className="bank-connected-title">
                    {isFailure ? failureTitle : t('bankConnectedTitle', { bankName: displayBankName })}
                </h1>
                <p className="bank-connected-desc">
                    {isFailure
                        ? t('bankConnectedFailureDesc', "We'll take you back so you can try connecting again.")
                        : t('bankConnectedDesc', { bankName: displayBankName })}
                </p>

                {!isFailure && (
                    <div className="consent-details-card">
                        <h3 className="consent-details-heading">
                            {t('bankConnectedConsentHeading')}
                        </h3>
                        <ul className="consent-details-list">
                            <li>{t('bankConnectedConsentValidity')}</li>
                            <li>{t('bankConnectedConsentScopeInfo')}</li>
                            <li>{t('bankConnectedConsentScopePayment')}</li>
                            <li>{t('bankConnectedConsentReverify')}</li>
                        </ul>
                        <p className="consent-details-offboard">
                            {t('bankConnectedOffboardNote')}
                        </p>
                    </div>
                )}

                {isActivating && !isFailure && (
                    <div className="activation-status">
                        <p>{t('bankConnectedActivating') || 'Activating authorization...'}</p>
                    </div>
                )}

                {isFailure && (
                    <div className="error-message">
                        <p>{failureBody}</p>
                        <p>
                            {t('bankConnectedRedirectingShortly', 'Redirecting you back shortly…')}
                        </p>
                    </div>
                )}
            </div>
        </PageLayout>
    );
}

export default BankConnected;
